import { useState, useMemo, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import {
  Loader2, ArrowLeft, FilePlus, LayoutTemplate, Copy,
  Search, Check, Globe, FileText, MapPin, Bookmark,
} from 'lucide-react'
import { toast } from 'sonner'
import { useLayouts, useCreatePage, usePages } from '../api/hooks'
import { useTopics, useRegions } from '../hooks/useTaxonomy'
import type { PageKind, PageBlockInput, Page } from '../api/types'

// ─── Constants ────────────────────────────────────────────────────────────────

type StartMode = 'scratch' | 'layout' | 'clone'

const START_OPTIONS: { value: StartMode; icon: React.ElementType; label: string; description: string }[] = [
  { value: 'scratch',  icon: FilePlus,       label: 'From scratch',   description: 'Start with a blank canvas.' },
  { value: 'layout',   icon: LayoutTemplate, label: 'From layout',    description: 'Copy a saved layout template.' },
  { value: 'clone',    icon: Copy,           label: 'Clone existing', description: 'Copy a page\'s structure and content.' },
]

const KIND_OPTIONS: { value: PageKind; icon: React.ElementType; label: string; description: string }[] = [
  { value: 'front-page', icon: Globe,     label: 'Front Page',      description: 'The rotating home page. One may be live at a time.' },
  { value: 'topic',      icon: FileText,  label: 'Topic',           description: 'A permanent section page (e.g. Politics, Technology).' },
  { value: 'region',     icon: MapPin,    label: 'Region',          description: 'A geographic edition page (e.g. US West, Europe).' },
  { value: 'edition',    icon: Bookmark,  label: 'Special Edition', description: 'A named edition on a single topic (e.g. The War in Ukraine). Multiple editions can be live at once.' },
]

const STATUS_LABEL: Record<Page['status'], string> = {
  live: 'Live', scheduled: 'Scheduled', draft: 'Draft', archived: 'Archived',
}

const STATUS_CLASS: Record<Page['status'], string> = {
  live:      'text-green-600 bg-green-50 border-green-200',
  scheduled: 'text-accent-primary-fg bg-accent-primary-fill border-accent-primary-solid/30',
  draft:     'text-fg-secondary bg-fill border-line',
  archived:  'text-fg-secondary bg-fill border-line opacity-50',
}

const KIND_LABEL_MAP: Record<PageKind, string> = {
  'front-page': 'Front Page', topic: 'Topic', region: 'Region', edition: 'Special Edition', book: 'Book',
}

// ─── Clone Picker ─────────────────────────────────────────────────────────────

function ClonePicker({ selected, onSelect }: { selected: Page | null; onSelect: (p: Page) => void }) {
  const [search, setSearch] = useState('')
  const { data: pages, isLoading } = usePages()

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const active = pages?.filter((p) => p.status !== 'archived') ?? []
    return q ? active.filter((p) => p.title.toLowerCase().includes(q)) : active
  }, [pages, search])

  return (
    <div>
      <div className="relative mb-2">
        <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-secondary" aria-hidden="true" />
        <input
          type="search"
          placeholder="Search pages…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 w-full rounded border border-line bg-canvas pl-7 pr-3 text-xs text-fg placeholder:text-fg-secondary focus:outline-none focus:ring-1 focus:ring-line-strong"
        />
      </div>
      {isLoading ? (
        <div className="flex h-16 items-center justify-center text-xs text-fg-secondary">Loading pages…</div>
      ) : (
        <ul className="max-h-64 overflow-y-auto divide-y divide-line rounded border border-line">
          {filtered.length === 0 ? (
            <li className="px-4 py-3 text-xs italic text-fg-secondary">No pages found.</li>
          ) : (
            filtered.map((page) => {
              const active = selected?.id === page.id
              return (
                <li key={page.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(page)}
                    className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      active ? 'bg-accent-primary-fill' : 'bg-canvas hover:bg-fill'
                    }`}
                  >
                    <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                      active ? 'border-accent-primary-solid bg-accent-primary-solid' : 'border-line'
                    }`}>
                      {active && <Check size={10} className="text-white" aria-hidden="true" />}
                    </div>
                    <span className={`flex-1 min-w-0 truncate text-xs font-semibold ${active ? 'text-accent-primary-fg' : 'text-fg'}`}>
                      {page.title}
                    </span>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <span className="text-[10px] text-fg-secondary">{KIND_LABEL_MAP[page.kind]}</span>
                      <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${STATUS_CLASS[page.status]}`}>
                        {STATUS_LABEL[page.status]}
                      </span>
                    </div>
                  </button>
                </li>
              )
            })
          )}
        </ul>
      )}
    </div>
  )
}

// ─── NewPagePage ──────────────────────────────────────────────────────────────

function NewPagePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const cloneFromId = searchParams.get('cloneFrom')
  const [mode, setMode] = useState<StartMode>(cloneFromId ? 'clone' : 'scratch')
  const [title, setTitle] = useState('')
  const [kind, setKind] = useState<PageKind>('front-page')
  const [slug, setSlug] = useState<string>('')
  const [layoutId, setLayoutId] = useState<string>('')
  const [cloneSource, setCloneSource] = useState<Page | null>(null)

  const { data: topics = [] } = useTopics()
  const { data: regions = [] } = useRegions()

  const { data: layouts } = useLayouts({ usageContext: 'official' })
  const { data: allPages } = usePages()
  const { mutate: createPage, isPending } = useCreatePage()

  // Pre-select clone source when navigated from a card's Clone button
  useEffect(() => {
    if (!cloneFromId || !allPages || cloneSource) return
    const match = allPages.find((p) => p.id === cloneFromId)
    if (match) handleSelectClone(match)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloneFromId, allPages])

  const needsSlug = kind === 'topic' || kind === 'region'
  const needsIdentifier = kind === 'edition'

  const availableSlugs = useMemo(() => {
    const usedSlugs = new Set(
      allPages?.filter((p) => p.kind === kind && p.status !== 'archived').map((p) => p.slug),
    )
    const taxonomyItems = kind === 'topic' ? topics : regions
    return {
      items: taxonomyItems.filter((t) => !usedSlugs.has(t.slug)),
      defaultTaken: usedSlugs.has('default'),
    }
  }, [allPages, kind, topics, regions])

  const canSubmit =
    title.trim() !== '' &&
    !isPending &&
    (!needsSlug || slug !== '') &&
    (!needsIdentifier || slug !== '') &&
    (mode !== 'layout' || layoutId !== '') &&
    (mode !== 'clone' || cloneSource !== null)

  function handleModeChange(next: StartMode) {
    setMode(next)
    setLayoutId('')
    setCloneSource(null)
  }

  function handleKindChange(next: PageKind) {
    setKind(next)
    setSlug('')
  }

  function handleSelectClone(page: Page) {
    setCloneSource(page)
    setTitle(`Copy of ${page.title}`)
    setKind(page.kind === 'book' ? 'front-page' : page.kind)
  }

  function buildBlocks(): PageBlockInput[] {
    if (mode === 'layout') {
      const chosen = layouts?.find((t) => t.id === layoutId)
      if (chosen) {
        return chosen.blocks
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((b) => {
            const columnContent = b.columnBlocks
              ? Object.fromEntries(
                  Object.entries(b.columnBlocks).map(([colKey, colBlocks]) => [
                    colKey,
                    (colBlocks ?? []).map((cb) => ({ id: cb.id, type: cb.type })),
                  ]),
                )
              : undefined
            return {
              id: b.id,
              type: b.type,
              order: b.order,
              config: b.config,
              ...(columnContent && Object.keys(columnContent).length > 0 ? { columnContent } : {}),
            }
          })
      }
    }
    if (mode === 'clone' && cloneSource) {
      return cloneSource.blocks
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((b) => ({
          id: crypto.randomUUID(),
          type: b.type,
          order: b.order,
          config: b.config,
          ...(b.content       && { content: b.content }),
          ...(b.columnContent && { columnContent: b.columnContent }),
        }))
    }
    // scratch — seed one story-row so the API's min-block requirement is met
    return [{ id: crypto.randomUUID(), type: 'story-row' as const, order: 0, config: {} }]
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return

    const blocks = buildBlocks()
    const originLayoutId =
      mode === 'layout'  ? layouts?.find((t) => t.id === layoutId)?.id :
      mode === 'clone'   ? cloneSource?.originLayoutId :
      undefined

    const derivedPool =
      kind === 'topic' ? { topicSlug: slug } :
      kind === 'region' ? { regionSlug: slug } :
      undefined
    createPage(
      { kind, slug: (needsSlug || needsIdentifier) ? slug : crypto.randomUUID(), title: title.trim(), blocks, originLayoutId, pool: derivedPool },
      {
        onSuccess: (page) => navigate(`/admin/pages/${page.id}`),
        onError: (err) => {
          const data = (err as { response?: { data?: { resolution?: string; error?: string } } }).response?.data
          const message = data?.resolution ?? data?.error ?? 'Failed to create page'
          toast.error('Could not create page', { description: message })
        },
      },
    )
  }

  return (
    <div className="relative min-h-screen">

      {/* Hero gradient wash */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-56"
        style={{ background: 'linear-gradient(to bottom, var(--violet-4), var(--violet-2), transparent)', opacity: 0.5 }}
      />

      {/* Header — matches PageListPage spacing exactly */}
      <div className="relative px-6 pt-5">
        <div className="flex items-center justify-between border-b border-line pb-4">
          <h1 className="font-zodiak pl-8 text-3xl font-bold text-fg">New Page</h1>
          <Link
            to="/admin/pages"
            className="flex items-center gap-1.5 text-xs text-fg-secondary transition-colors hover:text-fg"
          >
            <ArrowLeft size={12} aria-hidden="true" />
            Back to Pages
          </Link>
        </div>
      </div>

      {/* Form content */}
      <div className="relative mx-auto max-w-4xl px-8 pt-8">
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-10">

          {/* ── Left column: Page Type + Title + conditional fields ── */}
          <div className="space-y-8">

            {/* Kind — first, because it determines which fields appear below */}
            <div>
              <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Page Type</div>
              <div className="space-y-2">
                {KIND_OPTIONS.map(({ value, label, description }) => (
                  <label
                    key={value}
                    className={`flex cursor-pointer items-start gap-3 rounded border p-3 transition-colors ${
                      kind === value
                        ? 'border-accent-primary-solid bg-accent-primary-fill'
                        : 'border-line bg-canvas hover:bg-fill'
                    }`}
                  >
                    <input
                      type="radio"
                      name="kind"
                      value={value}
                      checked={kind === value}
                      onChange={() => handleKindChange(value)}
                      className="mt-0.5 accent-current"
                    />
                    <div>
                      <div className={`text-xs font-semibold ${kind === value ? 'text-accent-primary-fg' : 'text-fg'}`}>
                        {label}
                      </div>
                      <div className="mt-0.5 text-[11px] text-fg-secondary">{description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <label
                htmlFor="page-title"
                className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary"
              >
                Title
              </label>
              <input
                id="page-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Wednesday Morning Edition"
                className="w-full rounded border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder:text-fg-secondary focus:border-accent-primary-solid focus:outline-none"
              />
            </div>

            {/* Slug — topic/region only */}
            {needsSlug && (
              <div>
                <label
                  htmlFor="page-slug"
                  className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary"
                >
                  {kind === 'topic' ? 'Topic' : 'Region'}
                </label>
                {availableSlugs.items.length === 0 && availableSlugs.defaultTaken ? (
                  <p className="text-xs italic text-fg-secondary">All {kind === 'topic' ? 'topics' : 'regions'} already have pages. Archive an existing page first.</p>
                ) : (
                  <select
                    id="page-slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    className="w-full rounded border border-line bg-canvas px-3 py-2 text-sm text-fg focus:border-accent-primary-solid focus:outline-none"
                  >
                    <option value="">— Select —</option>
                    {!availableSlugs.defaultTaken && (
                      <option value="default">Default (fallback for all {kind === 'topic' ? 'topics' : 'regions'})</option>
                    )}
                    {availableSlugs.items.map((item) => (
                      <option key={item.slug} value={item.slug}>{item.label}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Identifier — edition only */}
            {needsIdentifier && (
              <div>
                <label
                  htmlFor="page-identifier"
                  className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary"
                >
                  Identifier
                </label>
                <input
                  id="page-identifier"
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  placeholder="e.g. war-in-ukraine"
                  className="w-full rounded border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder:text-fg-secondary focus:border-accent-primary-solid focus:outline-none"
                />
                <p className="mt-1.5 text-[11px] text-fg-secondary">
                  This is how the WIRe knows to replace previous editions; it will also appear in the URL. Use lowercase letters and hyphens.
                </p>
              </div>
            )}

          </div>

          {/* ── Right column: Starting Point + conditional picker ── */}
          <div className="space-y-8">

            {/* Starting point */}
            <div>
              <div className="mb-3 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                Starting Point
              </div>
              <div className="grid grid-cols-3 gap-2">
                {START_OPTIONS.map(({ value, icon: Icon, label, description }) => {
                  const active = mode === value
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleModeChange(value)}
                      className={`flex flex-col items-start gap-1.5 rounded border p-3 text-left transition-colors ${
                        active ? 'border-accent-primary-solid bg-accent-primary-fill' : 'border-line bg-canvas hover:bg-fill'
                      }`}
                    >
                      <Icon size={14} aria-hidden="true" className={active ? 'text-accent-primary-fg' : 'text-fg-secondary'} />
                      <span className={`text-xs font-semibold ${active ? 'text-accent-primary-fg' : 'text-fg'}`}>{label}</span>
                      <span className="text-[10px] leading-tight text-fg-secondary">{description}</span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Layout picker */}
            {mode === 'layout' && (
              <div>
                <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                  Layout Template
                </div>
                {!layouts ? (
                  <div className="flex h-12 items-center justify-center text-xs text-fg-secondary">Loading layouts…</div>
                ) : layouts.length === 0 ? (
                  <p className="text-xs italic text-fg-secondary opacity-60">No layout templates available.</p>
                ) : (
                  <div className="space-y-2">
                    {layouts.map((layout) => {
                      const active = layoutId === layout.id
                      return (
                        <label
                          key={layout.id}
                          className={`flex cursor-pointer items-start gap-3 rounded border p-3 transition-colors ${
                            active ? 'border-accent-primary-solid bg-accent-primary-fill' : 'border-line bg-canvas hover:bg-fill'
                          }`}
                        >
                          <input
                            type="radio"
                            name="layoutId"
                            value={layout.id}
                            checked={active}
                            onChange={() => setLayoutId(layout.id)}
                            className="mt-0.5 accent-current"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-semibold ${active ? 'text-accent-primary-fg' : 'text-fg'}`}>
                                {layout.name}
                              </span>
                              {layout.usageContext?.map((ctx) => (
                                <span
                                  key={ctx}
                                  className="rounded border border-line bg-fill px-1.5 py-0.5 text-[10px] font-semibold text-fg-secondary"
                                >
                                  {ctx === 'official' ? 'Front Page' : 'Book'}
                                </span>
                              ))}
                            </div>
                            {layout.description && (
                              <div className="mt-0.5 text-[11px] text-fg-secondary">{layout.description}</div>
                            )}
                          </div>
                        </label>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Clone picker */}
            {mode === 'clone' && (
              <div>
                <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                  Clone Source
                </div>
                <ClonePicker selected={cloneSource} onSelect={handleSelectClone} />
              </div>
            )}

          </div>
        </div>

        {/* Submit */}
        <div className="mt-10 flex justify-end border-t border-line pt-6">
          <button
            type="submit"
            disabled={!canSubmit}
            className="flex items-center gap-2 rounded border border-line bg-canvas px-5 py-2 text-sm font-semibold text-fg transition-colors hover:bg-fill disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isPending && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
            {isPending ? 'Creating…' : 'Create Page'}
          </button>
        </div>

      </form>
      </div>
    </div>
  )
}

export default NewPagePage
